/**
 * Your channels, as a folio (desk.css FOLIO): every platform is a folder tab, and the open one
 * is cut into the top edge of the sheet beneath it — paper, joined, with a stripe in the
 * platform's own ink — while the others are manila stock behind it. The sheet is that
 * platform's desk: its account line and its refresh, what Bearri can read there (on YouTube,
 * the Studio numbers), and its recent uploads with the sponsors found in them.
 *
 * Every sheet keeps the two levels of a platform apart, the same way on every platform: what's
 * PUBLIC (the profile, its posts, the sponsors found in them — what anyone who looks the creator
 * up can see) and what's ONLY YOU (private stats the creator connects, like YouTube's Studio
 * numbers). Connecting is always one big slab in the platform's own ink, asked once.
 *
 * A platform that isn't on Bearri yet is a tab too, drawn dashed in its own ink (the one dashed
 * box the desk allows), and its sheet is how to add it. The leftmost platform with a profile
 * opens by default. One sheet is always open: choosing another tab moves the join and swaps the
 * sheet in place — it never folds shut and reopens.
 *
 * On a phone the tabs stay ONE row of compact tabs (mark + count) and the sheet always opens
 * right under the row. Opening in place inside a 2×2 grid would put the sheet between rows and
 * make everything below it jump.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { PlatformInfo, UploadItem } from './sections';
import { ContentTile, PlatIcon, Skeleton, UploadSkeleton, compact } from './sections';
import { PlatformCta, PrivacyTag, WhatYouGet } from './PlatformCta';
import PlatformRefresh from '../creator/PlatformRefresh';
import { canScanSponsors } from '../../utils/researchFreshness';
import './channelFolio.css';

type PlatformId = PlatformInfo['id'];

export interface ChannelFolioProps {
  /** Every platform with a place on the shelf, in shelf order. */
  platforms: PlatformInfo[];
  uploads: UploadItem[];
  loading?: boolean;
  /** Open this platform first (back from Google, say) instead of the leftmost one. */
  initialPlatform?: PlatformId | null;
  /** What a platform's sheet can show beyond its profile — today, YouTube's Studio numbers. */
  analytics?: Partial<Record<PlatformId, React.ReactNode>>;
  refreshing?: boolean;
  refreshingPlatforms?: Set<string>;
  onRefreshPlatform?: (platform: string, handle: string) => void;
  onResearchUpdated?: () => void;
  onConnect?: (platformId: string) => void | Promise<void>;
  onLinkByHandle?: (platform: string, handle: string) => Promise<void> | void;
  onUnlinkHandle?: (platform: string) => void;
}

const hasProfile = (p: PlatformInfo) => p.connected || Boolean(p.saved);
const unit = (p: PlatformInfo) => (p.id === 'youtube' ? 'subscribers' : 'followers');

/** What an empty tab says it would add, in a few words (its sheet lists it item by item). */
const EMPTY_TAB_LINE: Record<PlatformId, string> = {
  youtube: 'Connect for your Studio numbers',
  instagram: 'Connect to check your posts',
  tiktok: 'Add by @handle',
  twitch: 'Add by @handle',
};

/** The private half of a sheet where Bearri has no private stats to show: said, not faked. */
const NO_PRIVATE_STATS: Partial<Record<PlatformId, string>> = {
  instagram: "Instagram's private stats — reach, audience, how each post did — need a permission Bearri doesn't ask for yet.",
  tiktok: "TikTok doesn't share private stats with Bearri yet, so this sheet shows what's public.",
  twitch: "Twitch doesn't share private stats with Bearri yet, so this sheet shows what's public.",
};

const ChannelFolio: React.FC<ChannelFolioProps> = ({
  platforms, uploads, loading = false, initialPlatform = null, analytics,
  refreshing, refreshingPlatforms, onRefreshPlatform, onResearchUpdated, onConnect, onLinkByHandle, onUnlinkHandle,
}) => {
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<PlatformId | null>(null);
  const tabRefs = useRef<Partial<Record<PlatformId, HTMLButtonElement | null>>>({});
  const sheetRef = useRef<HTMLDivElement>(null);
  const heightBefore = useRef<number | null>(null);

  const present = (id: PlatformId | null | undefined) => (id && platforms.some(p => p.id === id) ? id : null);
  const current: PlatformId | null = present(chosen) ?? present(initialPlatform)
    ?? platforms.find(hasProfile)?.id ?? platforms[0]?.id ?? null;

  const choose = (id: PlatformId, focus = false) => {
    if (id !== current) {
      heightBefore.current = sheetRef.current?.offsetHeight ?? null;
      setChosen(id);
    }
    if (focus) tabRefs.current[id]?.focus();
  };

  // The sheet eases from the old page's height to the new one's, so the page below glides
  // instead of jumping; then it lets go and grows with its content again.
  useLayoutEffect(() => {
    const el = sheetRef.current;
    const from = heightBefore.current;
    heightBefore.current = null;
    if (!el || from == null) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const to = el.offsetHeight;
    if (Math.abs(to - from) < 2) return;
    el.style.height = `${from}px`;
    el.style.overflow = 'hidden';
    void el.offsetHeight;
    el.style.transition = 'height 320ms cubic-bezier(.2,.8,.2,1)';
    el.style.height = `${to}px`;
    const settle = () => { el.style.height = ''; el.style.overflow = ''; el.style.transition = ''; };
    const timer = window.setTimeout(settle, 360);
    return () => { window.clearTimeout(timer); settle(); };
  }, [current]);

  // One click from the sheet to the platform's sign-in; without a handler, the Connections tab.
  const connectFor = (id: PlatformId) => async () => {
    if (onConnect) await onConnect(id);
    else navigate('/app/profile?tab=connections');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!current) return;
    const i = platforms.findIndex(p => p.id === current);
    const to = e.key === 'ArrowRight' ? (i + 1) % platforms.length
      : e.key === 'ArrowLeft' ? (i - 1 + platforms.length) % platforms.length
      : e.key === 'Home' ? 0 : e.key === 'End' ? platforms.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    choose(platforms[to].id, true);
  };

  // Until the data lands a tab can't say "not connected" (it isn't known yet): skeletons only.
  if (loading) {
    return (
      <div className="br-channels" aria-busy="true">
        <div className="br-channels__tabs" aria-hidden="true">
          {[0, 1].map(i => (
            <div key={i} className="br-channels__tab br-channels__tab--ghost">
              <Skeleton className="h-3.5 w-20" /><Skeleton className="mt-2 h-6 w-16" />
            </div>
          ))}
        </div>
        <div className="br-channels__sheet">
          <Skeleton className="h-4 w-48" />
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">{[0, 1, 2, 3].map(i => <UploadSkeleton key={i} />)}</div>
        </div>
      </div>
    );
  }
  if (!current) return null;

  return (
    <div className="br-channels">
      <div role="tablist" aria-label="Your channels" className="br-channels__tabs" onKeyDown={onKeyDown}>
        {platforms.map(p => {
          const open = p.id === current;
          const profile = hasProfile(p);
          const count = typeof p.followers === 'number' ? compact(p.followers) : null;
          const line = !profile ? EMPTY_TAB_LINE[p.id]
            : p.sample ? 'Sample profile' : p.handle ? `@${p.handle}` : 'Connected';
          return (
            <button key={p.id} ref={el => { tabRefs.current[p.id] = el; }} id={`channel-tab-${p.id}`}
              type="button" role="tab" aria-selected={open} aria-controls={`channel-sheet-${p.id}`} tabIndex={open ? 0 : -1}
              aria-label={`${p.label}${profile ? (count ? `, ${count} ${unit(p)}` : '') : ', not connected'}`}
              className="br-channels__tab" data-platform={p.id} data-state={profile ? 'profile' : 'empty'}
              onClick={() => choose(p.id)}>
              <span className="br-channels__tab-head">
                {profile ? <span className="br-channels__tab-mark"><PlatIcon id={p.id} size={18} /></span> : (
                  <span className="br-platform-slot__stamp br-channels__tab-stamp" aria-hidden="true">
                    <PlatIcon id={p.id} size={20} fill="currentColor" /><span className="br-platform-slot__plus" />
                  </span>
                )}
                <span className="br-channels__tab-name">{p.label}</span>
              </span>
              <span className="br-channels__tab-line">{line}</span>
              {profile && (
                <span className="br-channels__tab-count">
                  <span className="br-num">{count ?? '—'}</span><span className="br-channels__tab-unit">{unit(p)}</span>
                </span>
              )}
              {/* a phone keeps only the mark and one word or figure */}
              <span className="br-channels__tab-compact" aria-hidden="true">{profile ? (count ?? '—') : 'Add'}</span>
            </button>
          );
        })}
      </div>

      <div ref={sheetRef} className="br-channels__sheet">
        {platforms.map(p => (
          <div key={p.id} id={`channel-sheet-${p.id}`} role="tabpanel" aria-labelledby={`channel-tab-${p.id}`}
            hidden={p.id !== current} className="br-channels__page">
            {hasProfile(p) ? (
              <ProfileSheet p={p} uploads={uploads.filter(u => u.platform === p.id)}
                analytics={p.sample ? null : analytics?.[p.id] ?? null}
                busy={Boolean(refreshing || refreshingPlatforms?.has(p.id))}
                onRefreshPlatform={onRefreshPlatform} onResearchUpdated={onResearchUpdated}
                onConnect={connectFor(p.id)} onUnlinkHandle={onUnlinkHandle} />
            ) : (
              <ConnectSheet p={p} onConnect={connectFor(p.id)} onLinkByHandle={onLinkByHandle} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
export default ChannelFolio;

// ── the sheet of a platform with a profile ──────────────────────────────────────────────
const ProfileSheet: React.FC<{
  p: PlatformInfo; uploads: UploadItem[]; analytics: React.ReactNode; busy: boolean;
  onRefreshPlatform?: (platform: string, handle: string) => void; onResearchUpdated?: () => void;
  onConnect: () => Promise<void>; onUnlinkHandle?: (platform: string) => void;
}> = ({ p, uploads, analytics, busy, onRefreshPlatform, onResearchUpdated, onConnect, onUnlinkHandle }) => {
  const how = p.sample ? 'sample profile'
    : p.connectVia === 'handle' ? 'added by its @handle'
    : p.id === 'instagram' && p.connected ? 'connected with Instagram'
    : 'public profile';
  const canCheck = canScanSponsors(p.id) && Boolean(p.handle) && !p.sample && Boolean(onRefreshPlatform);
  // YouTube's private half is the Studio block, which knows from the server whether YouTube is
  // connected (and asks to connect, once, if not). Elsewhere the sheet says what it can.
  const privateHalf = analytics ? analytics
    : p.sample ? null
    : p.id === 'instagram' && !p.connected ? <ConnectSlab p={p} onConnect={onConnect} />
    : NO_PRIVATE_STATS[p.id] ? (
      <div>
        <h3 className="br-h3">Private stats<PrivacyTag kind="private" /></h3>
        <p className="br-small mt-1">{NO_PRIVATE_STATS[p.id]}</p>
      </div>
    ) : null;
  return (
    <>
      <div className="br-channels__account">
        <div className="min-w-0">
          <p className="br-channels__who">{p.handle ? `@${p.handle}` : p.label}<PrivacyTag kind="public" /></p>
          <p className="br-caption">
            {typeof p.followers === 'number' ? `${p.followers.toLocaleString('en-US')} ${unit(p)} · ` : ''}{how}
          </p>
          {p.connectVia === 'handle' && onUnlinkHandle && (
            <div className="br-channels__account-links">
              <button type="button" onClick={() => onUnlinkHandle(p.id)} title={`Unlink @${p.handle || ''} — you can enter a different handle`}
                className="br-remove br-caption p-0 font-semibold">not yours? unlink</button>
            </div>
          )}
        </div>
        {canCheck && (
          <div className="br-channels__refresh">
            <PlatformRefresh platform={p.id} handle={p.handle!} busy={busy}
              onCheck={() => onRefreshPlatform!(p.id, p.handle!)} onUpdated={onResearchUpdated} />
          </div>
        )}
      </div>

      {privateHalf && <div className="br-channels__block">{privateHalf}</div>}

      <div className="br-channels__block">
        <div className="br-channels__block-head">
          <h3 className="br-h3">Recent uploads</h3>
          {uploads.length > 0 && <span className="br-small">Brands detected in each</span>}
        </div>
        {uploads.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {uploads.slice(0, 4).map(u => (
              <ContentTile key={u.id} url={u.url} title={u.title} thumbnail={u.thumbnail} platform={u.platform} brands={u.brands}
                footNote={u.scraped ? 'No sponsors detected' : 'Not analyzed yet'} />
            ))}
          </div>
        ) : (
          <p className="br-caption">No {p.label} uploads synced yet.{canCheck ? ' Check for new posts to find your content and the sponsors in it.' : ''}</p>
        )}
      </div>
    </>
  );
};

/** Connecting, asked once: the big slab in the platform's ink and what it gives. */
const ConnectSlab: React.FC<{ p: PlatformInfo; onConnect: () => Promise<void> }> = ({ p, onConnect }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConnect(); } finally { setBusy(false); }
  };
  return (
    <div className="br-channels__connect">
      <WhatYouGet id={p.id} heading={`Connecting ${p.label} adds`} />
      <div className="mt-4"><PlatformCta id={p.id} label={`Connect ${p.label}`} busy={busy} busyLabel="Opening…" onClick={go} /></div>
    </div>
  );
};

// ── the sheet of a platform that isn't on Bearri yet: how to add it ─────────────────────
const ConnectSheet: React.FC<{
  p: PlatformInfo; onConnect: () => Promise<void>; onLinkByHandle?: (platform: string, handle: string) => Promise<void> | void;
}> = ({ p, onConnect, onLinkByHandle }) => {
  const [handle, setHandle] = useState('');
  const [linking, setLinking] = useState(false);
  if (p.connectVia !== 'handle') return <ConnectSlab p={p} onConnect={onConnect} />;
  const submit = async () => {
    const h = handle.trim().replace(/^@/, '');
    if (!h || !onLinkByHandle) return;
    setLinking(true);
    try {
      await onLinkByHandle(p.id, h);
      setHandle('');
    } finally {
      setLinking(false);
    }
  };
  return (
    <div className="br-channels__connect">
      <WhatYouGet id={p.id} heading={`Adding your ${p.label} by its @handle gives you`} />
      {onLinkByHandle ? (
        <form className="br-channels__add" onSubmit={e => { e.preventDefault(); void submit(); }}>
          <input type="text" value={handle} onChange={e => setHandle(e.target.value)} placeholder="@handle"
            aria-label={`Your ${p.label} handle`} className="br-line-input" />
          <PlatformCta id={p.id} type="submit" label={`Add ${p.label}`} busy={linking} busyLabel="Adding…" disabled={!handle.trim()} />
        </form>
      ) : null}
    </div>
  );
};
